// « Instructions de cette étape » — the Brain rules that belong to one step (owner, 2026-10-15):
//   « I click a step, I see the instructions of this exact step from the Brain and I can
//     change them — or add a new one, mixed with the others in a logical way, never
//     contradicting the other rules. »
//
// ⚠️ THE BRAIN IS LIVE. Every enabled instruction reaches the reply model today, so an
// edit here changes the agent at once — said on screen, next to every save button.
// Only the LINK (which rules belong to this step) waits for « Enregistrer la version ».
//
// ⚠️ A CONTRADICTION BLOCKS « Appliquer ». The check is the model's (worker
// lib/process/stepInstructions.ts); the way out is to rephrase, or fix the other rule
// right here, then check again.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useT } from '@/lib/i18n';
import { whatsappApi, type AssistantKnowledge } from '@/lib/api';
import { processApi, type MergeResult } from '@/lib/processApi';

const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));

function EntryEditor({ entry, onDone }: { entry: AssistantKnowledge; onDone: () => void }) {
  const { t } = useT();
  const qc = useQueryClient();
  const [title, setTitle] = useState(entry.title ?? '');
  const [body, setBody] = useState(entry.body ?? '');
  const save = useMutation({
    mutationFn: () => whatsappApi.updateKnowledge(entry.id, { title: title.trim(), body: body.trim() }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['assistant-knowledge'] }); onDone(); },
  });
  return (
    <div className="flex flex-col gap-2">
      <label className="sr-only" htmlFor={`kb-t-${entry.id}`}>{t('Titre')}</label>
      <input id={`kb-t-${entry.id}`} value={title} onChange={(e) => setTitle(e.target.value)}
        className="h-10 rounded-lg border border-gray-300 bg-white px-3 text-[14px] font-semibold text-gray-900" />
      <label className="sr-only" htmlFor={`kb-b-${entry.id}`}>{t('Instruction')}</label>
      <textarea id={`kb-b-${entry.id}`} value={body} onChange={(e) => setBody(e.target.value)} rows={6}
        className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-[13.5px] leading-relaxed text-gray-900" />
      {save.isError && <p role="alert" className="text-[12.5px] text-red-800">{errText(save.error)}</p>}
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" disabled={!title.trim() || !body.trim() || save.isPending} onClick={() => save.mutate()}
          className="h-10 px-4 rounded-lg bg-adm-gold text-[#2B2116] dark:text-[#2B2116] text-[13px] font-bold cursor-pointer disabled:opacity-50">
          {save.isPending ? t('Enregistrement…') : t('Enregistrer dans le Cerveau')}
        </button>
        <button type="button" onClick={onDone} className="h-10 px-3 rounded-lg text-[13px] font-semibold text-gray-700 hover:bg-gray-100 cursor-pointer">{t('Annuler')}</button>
        <span className="text-[11.5px] text-gray-500">{t('Change l’agent tout de suite.')}</span>
      </div>
    </div>
  );
}

function EntryCard({ entry, onUnlink }: { entry: AssistantKnowledge; onUnlink: () => void }) {
  const { t } = useT();
  const [editing, setEditing] = useState(false);
  const [open, setOpen] = useState(false);
  return (
    <li className="border border-gray-200 rounded-xl px-4 py-3">
      {editing ? <EntryEditor entry={entry} onDone={() => setEditing(false)} /> : (
        <>
          <div className="flex flex-wrap items-start gap-x-2 gap-y-1">
            <h4 className="basis-full sm:basis-auto sm:flex-1 min-w-0 text-[13.5px] font-bold text-gray-900" style={{ fontFamily: "inherit" }}>{entry.title}</h4>
            <div className="flex items-center gap-1 shrink-0">
              <button type="button" onClick={() => setEditing(true)} className="shrink-0 h-9 px-3 rounded-lg border border-gray-300 text-[12.5px] font-semibold text-gray-800 hover:bg-gray-50 cursor-pointer">{t('Modifier')}</button>
              <button type="button" onClick={onUnlink} aria-label={t('Retirer « {x} » de cette étape', { x: entry.title ?? '' })}
                className="shrink-0 h-9 px-2 rounded-lg text-[12.5px] font-semibold text-gray-600 hover:bg-gray-100 cursor-pointer">{t('Retirer')}</button>
            </div>
          </div>
          <p className={`mt-1 text-[13px] leading-relaxed text-gray-700 whitespace-pre-line ${open ? '' : 'line-clamp-3'}`}>{entry.body}</p>
          {(entry.body ?? '').length > 220 && (
            <button type="button" onClick={() => setOpen(!open)} className="mt-1 text-[12px] font-semibold text-amber-900 cursor-pointer">{open ? t('Réduire') : t('Tout lire')}</button>
          )}
        </>
      )}
    </li>
  );
}

export function StepBrain({ stepKey, linkedIds, onLink }: {
  stepKey: string;
  linkedIds: string[];
  /** Changes the draft's link list (saved with the version). */
  onLink: (ids: string[]) => void;
}) {
  const { t } = useT();
  const qc = useQueryClient();
  const { data: kb } = useQuery({ queryKey: ['assistant-knowledge'], queryFn: () => whatsappApi.knowledge(), staleTime: 60_000 });
  const rules = (kb?.knowledge ?? []).filter((k) => k.kind === 'instruction' && k.enabled);
  const byId = new Map(rules.map((r) => [r.id, r]));
  const linked = linkedIds.map((id) => byId.get(id)).filter((r): r is AssistantKnowledge => !!r);
  const gone = linkedIds.filter((id) => kb && !byId.has(id));
  const unlinked = rules.filter((r) => !linkedIds.includes(r.id));

  const [suggested, setSuggested] = useState<string[] | null>(null);
  const suggest = useMutation({
    mutationFn: () => processApi.suggestInstructions(stepKey),
    onSuccess: (r) => setSuggested(r.ids.filter((id) => !linkedIds.includes(id))),
  });

  const [draft, setDraft] = useState('');
  const [proposal, setProposal] = useState<MergeResult | null>(null);
  const [fixing, setFixing] = useState<string | null>(null);
  const merge = useMutation({
    mutationFn: () => processApi.mergeInstruction(stepKey, draft.trim(), linkedIds),
    onSuccess: (r) => setProposal(r),
  });
  const apply = useMutation({
    mutationFn: async (p: MergeResult) => {
      for (const ch of p.changes) await whatsappApi.updateKnowledge(ch.id, { body: ch.after });
      if (p.add) {
        const r = await whatsappApi.addKnowledge({ kind: 'instruction', title: p.add.title, body: p.add.body, section: 'general' });
        return r.entry.id;
      }
      return null;
    },
    onSuccess: (newId) => {
      if (newId) onLink([...linkedIds, newId]);
      qc.invalidateQueries({ queryKey: ['assistant-knowledge'] });
      setProposal(null); setDraft('');
    },
  });
  const blocked = (proposal?.conflicts.length ?? 0) > 0;

  return (
    <section aria-labelledby={`brain-h-${stepKey}`} className="min-w-0 border border-gray-200 rounded-xl px-4 py-3 flex flex-col gap-3">
      <div>
        <h3 id={`brain-h-${stepKey}`} className="text-[13px] font-bold text-gray-900" style={{ fontFamily: "inherit" }}>{t('Instructions de cette étape')}</h3>
        <p className="text-[11.5px] text-gray-500 mt-0.5">{t('Tirées du Cerveau. Une modification change l’agent tout de suite ; la liste des instructions liées est gardée avec la version.')}</p>
      </div>

      {linked.length > 0 ? (
        <ul className="flex flex-col gap-2">
          {linked.map((e) => <EntryCard key={e.id} entry={e} onUnlink={() => onLink(linkedIds.filter((x) => x !== e.id))} />)}
        </ul>
      ) : (
        <p className="text-[12.5px] text-gray-600">{t('Aucune instruction liée à cette étape pour l’instant.')}</p>
      )}
      {gone.length > 0 && (
        <p className="text-[12px] text-amber-900">
          {t('{n} instruction(s) liée(s) n’existent plus ou sont désactivées dans le Cerveau.', { n: gone.length })}{' '}
          <button type="button" onClick={() => onLink(linkedIds.filter((id) => !gone.includes(id)))} className="font-semibold underline cursor-pointer">{t('Retirer')}</button>
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <button type="button" disabled={suggest.isPending} onClick={() => suggest.mutate()}
          className="h-10 px-3 rounded-lg border border-dashed border-adm-gold text-[13px] font-semibold text-amber-900 cursor-pointer disabled:opacity-50">
          {suggest.isPending ? t('Lecture du Cerveau…') : t('Proposer automatiquement')}
        </button>
        {unlinked.length > 0 && (
          <>
            <label htmlFor={`brain-pick-${stepKey}`} className="sr-only">{t('Lier une instruction du Cerveau')}</label>
            <select id={`brain-pick-${stepKey}`} value="" onChange={(e) => e.target.value && onLink([...linkedIds, e.target.value])}
              className="h-10 w-full sm:w-auto min-w-0 max-w-full rounded-lg border border-gray-300 bg-white px-2 text-[13px] text-gray-900">
              <option value="">{t('Lier une instruction du Cerveau…')}</option>
              {unlinked.map((r) => <option key={r.id} value={r.id}>{r.title}</option>)}
            </select>
          </>
        )}
      </div>
      {suggest.isError && <p role="alert" className="text-[12.5px] text-red-800">{errText(suggest.error)}</p>}
      {suggested && (
        <div className="rounded-lg bg-amber-50 px-3 py-2">
          {suggested.length === 0 ? <p className="text-[12.5px] text-amber-900">{t('L’IA ne voit pas d’autre instruction pour cette étape.')}</p> : (
            <>
              <p className="text-[12.5px] font-semibold text-amber-900">{t('L’IA propose de lier :')}</p>
              <ul className="mt-1 flex flex-col gap-1">
                {suggested.map((id) => byId.get(id) && (
                  <li key={id} className="flex items-center gap-2 text-[13px] text-gray-900">
                    <span className="flex-1 min-w-0">{byId.get(id)!.title}</span>
                    <button type="button" onClick={() => { onLink([...linkedIds, id]); setSuggested(suggested.filter((x) => x !== id)); }}
                      className="h-8 px-3 rounded-md bg-white border border-gray-300 text-[12px] font-semibold cursor-pointer">{t('Lier')}</button>
                  </li>
                ))}
              </ul>
              <button type="button" onClick={() => { onLink([...new Set([...linkedIds, ...suggested])]); setSuggested(null); }}
                className="mt-2 h-9 px-3 rounded-lg bg-adm-gold text-[#2B2116] dark:text-[#2B2116] text-[12.5px] font-bold cursor-pointer">{t('Tout lier ({n})', { n: suggested.length })}</button>
            </>
          )}
        </div>
      )}

      <div className="flex flex-col gap-2 border-t border-gray-100 pt-3">
        <label htmlFor={`brain-new-${stepKey}`} className="text-[13px] font-bold text-gray-900">{t('Ajouter une instruction')}</label>
        <textarea id={`brain-new-${stepKey}`} value={draft} rows={3} onChange={(e) => { setDraft(e.target.value); setProposal(null); }}
          placeholder={t('Ex. : demander au moins 20 photos par album, en document.')}
          className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-[13.5px] text-gray-900" />
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" disabled={draft.trim().length < 8 || merge.isPending} onClick={() => merge.mutate()}
            className="h-10 px-4 rounded-lg border border-gray-300 bg-white text-[13px] font-bold text-gray-900 cursor-pointer disabled:opacity-50">
            {merge.isPending ? t('Vérification…') : t('Vérifier et fusionner')}
          </button>
          <span className="text-[11.5px] text-gray-500">{t('Rien ne change avant « Appliquer ».')}</span>
        </div>
        {merge.isError && <p role="alert" className="text-[12.5px] text-red-800">{errText(merge.error)}</p>}
      </div>

      {proposal && (
        <div className="flex flex-col gap-2" aria-live="polite">
          {proposal.summary && <p className="text-[13px] text-gray-800">{proposal.summary}</p>}
          {proposal.conflicts.map((c, i) => (
            <div key={i} role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2">
              <p className="text-[13px] text-red-900"><strong>{t('Contradiction')}</strong>{c.title ? ` — « ${c.title} »` : ''} : {c.why}</p>
              {c.id && byId.get(c.id) && (fixing === c.id
                ? <div className="mt-2"><EntryEditor entry={byId.get(c.id)!} onDone={() => setFixing(null)} /></div>
                : <button type="button" onClick={() => setFixing(c.id)} className="mt-1 text-[12.5px] font-semibold text-red-900 underline cursor-pointer">{t('Modifier cette règle')}</button>)}
            </div>
          ))}
          {proposal.changes.map((ch) => (
            <div key={ch.id} className="rounded-lg border border-gray-200 px-3 py-2">
              <p className="text-[12.5px] font-bold text-gray-900">{t('Modifiée : « {x} »', { x: ch.title })}</p>
              <p className="mt-1 text-[12.5px] text-gray-500 line-through whitespace-pre-line">{ch.before}</p>
              <p className="mt-1 text-[13px] text-gray-900 bg-emerald-50 rounded px-2 py-1 whitespace-pre-line">{ch.after}</p>
            </div>
          ))}
          {proposal.add && (
            <div className="rounded-lg border border-gray-200 px-3 py-2">
              <p className="text-[12.5px] font-bold text-gray-900">{t('Nouvelle instruction : « {x} »', { x: proposal.add.title })}</p>
              <p className="mt-1 text-[13px] text-gray-900 bg-emerald-50 rounded px-2 py-1 whitespace-pre-line">{proposal.add.body}</p>
            </div>
          )}
          {apply.isError && <p role="alert" className="text-[12.5px] text-red-800">{errText(apply.error)}</p>}
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" disabled={blocked || apply.isPending || (!proposal.changes.length && !proposal.add)} onClick={() => apply.mutate(proposal)}
              className="h-10 px-4 rounded-lg bg-adm-gold text-[#2B2116] dark:text-[#2B2116] text-[13px] font-bold cursor-pointer disabled:opacity-50">
              {apply.isPending ? t('Enregistrement…') : t('Appliquer au Cerveau')}
            </button>
            <button type="button" onClick={() => setProposal(null)} className="h-10 px-3 rounded-lg text-[13px] font-semibold text-gray-700 hover:bg-gray-100 cursor-pointer">{t('Annuler')}</button>
            <span className="text-[11.5px] text-gray-500">
              {blocked ? t('Bloqué : reformulez ou corrigez la règle en conflit, puis vérifiez à nouveau.') : t('Change l’agent tout de suite.')}
            </span>
          </div>
        </div>
      )}
    </section>
  );
}
